import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const prettyDT = (iso) => {
  try { return new Date(iso).toLocaleString('hi-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const STATUS_STYLE = {
  scheduled: 'bg-blue-50 text-blue-700',
  rescheduled: 'bg-amber-50 text-amber-700',
  cancelled: 'bg-red-50 text-red-600',
  completed: 'bg-green-50 text-green-700',
};

export default function OnboardingAdminPanel({ token }) {
  const authToken = token || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${authToken}` };

  const [cal, setCal] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [stats, setStats] = useState({ total: 0, upcoming: 0 });
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/oauth/calendar/status`, { headers });
      setCal(await res.json());
    } catch (e) { /* noop */ }
  }, [authToken]);

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/onboarding-schedule/all`, { headers });
      const d = await res.json();
      setSchedules(d.schedules || []);
      setStats({ total: d.total || 0, upcoming: d.upcoming || 0 });
    } catch (e) { setSchedules([]); }
    finally { setLoading(false); }
  }, [authToken]);

  useEffect(() => {
    fetchStatus();
    fetchSchedules();
    // Detect OAuth callback result in URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar') === 'connected') { toast.success('✅ Google Calendar connect हो गया!'); fetchStatus(); }
    if (params.get('calendar') === 'error') toast.error('Google Calendar connect नहीं हो पाया।');
  }, [fetchStatus, fetchSchedules]);

  const connect = async () => {
    try {
      const res = await fetch(`${API_URL}/api/oauth/calendar/connect`, { headers });
      const d = await res.json();
      if (d.authorization_url) window.location.href = d.authorization_url;
      else toast.error(d.detail || 'Connect URL नहीं मिला');
    } catch (e) { toast.error('Connect विफल'); }
  };

  const disconnect = async () => {
    if (!window.confirm('Google Calendar disconnect करें?')) return;
    await fetch(`${API_URL}/api/oauth/calendar/disconnect`, { method: 'POST', headers });
    toast.success('Disconnected'); fetchStatus();
  };

  return (
    <div className="space-y-5" data-testid="onboarding-admin-panel">
      {/* Google Calendar status */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-sm font-bold text-slate-800">📅 Google Calendar (Meet links)</p>
            {cal === null ? (
              <p className="text-xs text-slate-400 mt-1">स्थिति लोड हो रही है...</p>
            ) : !cal?.configured ? (
              <p className="text-xs text-red-500 mt-1">⚠️ GOOGLE_CLIENT_ID / SECRET configured नहीं है।</p>
            ) : cal?.connected ? (
              <p className="text-xs text-green-600 mt-1" data-testid="calendar-connected">✅ Connected{cal.email ? ` — ${cal.email}` : ''}. हर booking पर असली Meet link बनेगा।</p>
            ) : (
              <p className="text-xs text-amber-600 mt-1" data-testid="calendar-not-connected">Google Calendar connected नहीं है — Meet links "pending" रहेंगे। कृपया connect करें।</p>
            )}
          </div>
          {cal?.connected ? (
            <button onClick={disconnect} className="text-xs px-3 py-2 rounded-lg border border-red-200 text-red-600" data-testid="calendar-disconnect-btn">Disconnect</button>
          ) : (
            <button onClick={connect} disabled={!cal?.configured} className="text-sm px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-40" data-testid="calendar-connect-btn">🔗 Connect Google Calendar</button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 rounded-xl p-4 text-center"><p className="text-2xl font-bold text-blue-700">{stats.upcoming}</p><p className="text-[11px] text-blue-500">आने वाली Calls</p></div>
        <div className="bg-slate-50 rounded-xl p-4 text-center"><p className="text-2xl font-bold text-slate-700">{stats.total}</p><p className="text-[11px] text-slate-500">कुल Bookings</p></div>
      </div>

      {/* Schedules list */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <p className="text-sm font-bold text-slate-800 mb-3">🗓️ Onboarding Schedule</p>
        {loading ? (
          <p className="text-center text-slate-400 text-sm py-6" data-testid="onboarding-loading">⏳ लोड हो रहा है...</p>
        ) : schedules.length === 0 ? (
          <p className="text-center text-slate-400 text-sm py-6" data-testid="onboarding-empty">अभी कोई onboarding booking नहीं।</p>
        ) : (
          <div className="space-y-2" data-testid="onboarding-list">
            {schedules.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-3 border border-slate-100 rounded-xl p-3" data-testid={`onboarding-row-${s.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{s.school_name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{s.user_name} · {s.user_phone}</p>
                  <p className="text-[11px] text-slate-400">{prettyDT(s.start_iso)} IST</p>
                  {s.demo_school_id && (
                    <p className="text-[10px] text-indigo-500 truncate" data-testid={`onboarding-demo-${s.id}`} title={s.demo_chat_session_id || ''}>🏫 Demo: {s.demo_school_id}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_STYLE[s.status] || 'bg-slate-100 text-slate-500'}`}>{s.status}</span>
                  {s.meet_link ? (
                    <a href={s.meet_link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-green-600 hover:underline">🔗 Meet</a>
                  ) : (
                    <span className="text-[10px] text-amber-500">Meet pending</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
